import { Link } from '@customink/pigment-react';
import Alert from '@customink/pigment-react/lib/components/Alert';
import type { SwatchData } from '@customink/pigment-react/lib/components/ColorSwatch/ColorSwatchGroup';
import { ColorSwatchGroup } from '@customink/pigment-react/lib/components/ColorSwatch/ColorSwatchGroup';
import type {
  Product,
  ProjectItem,
  Swatch,
} from '@customink/project-service-react-components';
import { useAtomValue } from 'jotai';

import { WCIBMO_NAMES } from '@/constants';
import { useStyle } from '@/hooks/Product';
import { usePatchUpdateItem } from '@/hooks/ProjectItem';
import { useOrderQuantityType } from '@/hooks/useOrderQuantityType';
import { hasValidZipCodeAtom } from '@/hooks/useValidateZipCode';
import { pageViewTag } from '@/utils/metrics';

type MinimumStyleQtyProps = {
  product?: Product;
  projectItem?: ProjectItem;
  onIncreaseQuantityClick?: () => void;
};

/**
 * The minimum order quantity is not met, but there is an alternative style
 * available with at least some no-minimum colors.
 */
export default function MinimumStyleQty({
  onIncreaseQuantityClick,
  product,
  projectItem,
}: MinimumStyleQtyProps) {
  const [updateProjectItem] = usePatchUpdateItem();
  const quantityType = useOrderQuantityType();
  const hasValidZipCode = useAtomValue(hasValidZipCodeAtom);

  const { data: alternativeProduct } = useStyle({
    styleId: product?.singles_alternative_id,
  });

  const updateItem = (swatch: SwatchData) => {
    if (!projectItem?.id) return;

    updateProjectItem(projectItem.id, {
      product: { colorId: swatch.value },
    });
  };

  const swatchColorData: SwatchData[] = alternativeProduct?.swatches
    ?.filter((swatch: Swatch) => swatch.singlesEnabled)
    .map((swatch: Swatch) => {
      const swatchData: SwatchData = {
        color: swatch.hex,
        trimRgb: swatch.trimRgb,
        value: swatch.colorId,
      };
      return swatchData;
    });

  if (!product || !alternativeProduct) return null;
  if (hasValidZipCode) {
    pageViewTag(
      `checkout/summary/${quantityType}/wcibmo/${WCIBMO_NAMES.RAISE_ABOVE_MIN_OR_STYLE}`,
    );
  }

  return (
    <>
      {!!alternativeProduct && (
        <Alert data-testid="raise-quantity" severity="error">
          <strong>
            The minimum order quantity for the {product.name} is{' '}
            {product.minimum_quantity}.
          </strong>{' '}
          Please either:
          <ul
            style={{
              fontSize: '14px',
              lineHeight: '1.5',
              marginTop: '4px',
              paddingLeft: '16px',
            }}
          >
            <li style={{ marginBottom: '4px' }}>
              <Link onClick={onIncreaseQuantityClick}>
                Increase the order quantity
              </Link>{' '}
              to {product.minimum_quantity} or more, or
            </li>
            <li>
              Try the {alternativeProduct.name} in one of these colors,
              available at any quantity. You can review your selection in the
              Design Lab.
            </li>
          </ul>
          <ColorSwatchGroup
            defaultSelected={false}
            onSwatchSelect={updateItem}
            swatchData={swatchColorData || []}
            sx={{ marginBottom: '8px' }}
          />
        </Alert>
      )}
    </>
  );
}
